/**
 * The validation status glyphs (WMC-565): ✓ on blue = validated (system),
 * ✓ on green = approved, ? on amber = review, ✕ on red = rejected, an empty
 * grey box = pending. The ✓ ? ✕ trio is lifted from the discography-IDs
 * certainty marker (CertaintyMarker.tsx) so the two columns match.
 */
import React from 'react';
import { Check, Loader2, Minus, X } from 'lucide-react';
import {
  AdminValidationStatus,
  STATUS_META,
  useValidationState,
  useValidationStore,
  validationStore,
  ValidationStatus,
} from './validationStore';

/** All three buttons are the same fixed square — the certainty marker's
 *  ✓ / ✕ are 14px (10px icon + 2px padding) and its "?" box is 14px too. */
const SIZES = {
  sm: { box: 14, icon: 10, font: 11 },
  lg: { box: 24, icon: 16, font: 17 },
} as const;

/** The glyph drawn for a status; its colour is the status's `STATUS_META.bg`. */
const statusIcon = (
  status: ValidationStatus,
  px: number,
  font: number
): React.ReactNode => {
  switch (status) {
    case 'validated':
    case 'approved':
      return <Check size={px} strokeWidth="4" />;
    case 'review':
      return (
        <span
          className="tw-font-bold"
          style={{ fontSize: font, lineHeight: 1 }}
        >
          ?
        </span>
      );
    case 'rejected':
      return <X size={px} strokeWidth="4" />;
    default:
      return <Minus size={px} strokeWidth="3" className="tw-opacity-60" />;
  }
};

/** One non-interactive icon for the row's current status, at the certainty
 *  marker's size (validation mode, where the only way to change a status is
 *  the selection + floating bar; bulk edit, where the column is read-only). */
export const ValidationStatusIcon: React.FC<{ rowId: string }> = ({
  rowId,
}) => {
  const st = useValidationState(rowId);
  const { box, icon: px, font } = SIZES.sm;
  const meta = STATUS_META[st.status];
  return (
    <span
      role="img"
      aria-label={meta.label}
      title={`${meta.label} · ${meta.hint}`}
      style={{ width: box, height: box }}
      className={[
        'tw-inline-flex tw-items-center tw-justify-center tw-rounded tw-text-white tw-flex-shrink-0',
        meta.bg,
      ].join(' ')}
    >
      {statusIcon(st.status, px, font)}
    </span>
  );
};

/**
 * ✓ ? ✕ for one row (`rowId`, the current status lit) or for a selection
 * (`rowIds`, nothing lit). `size` = 'sm' is the certainty-marker size;
 * 'lg' is used by the floating bar. A click writes through the store.
 */
export const ValidationTrio: React.FC<{
  rowId?: string;
  rowIds?: string[];
  size?: 'sm' | 'lg';
  disabled?: boolean;
}> = ({ rowId, rowIds, size = 'sm', disabled }) => {
  const ids = rowIds ?? (rowId ? [rowId] : []);
  const st = useValidationState(rowId ?? '');
  /** The status being saved on this row (spinner on that button). */
  const savingStatus = useValidationStore((s) =>
    rowId ? s.busy[rowId] : undefined
  );
  /** Any of the rows mid-save: the trio is locked until it lands. */
  const saving = useValidationStore((s) => ids.some((id) => !!s.busy[id]));

  const { box, icon: px, font } = SIZES[size];
  const current = rowId ? st.status : null;

  const btn = (status: AdminValidationStatus, title: string) => {
    // A validated row shows its ✓ on the approve button, in the system's
    // blue: approving it is the admin taking the system's verdict over.
    const takesOverValidated = status === 'approved' && current === 'validated';
    return (
      <button
        type="button"
        title={
          takesOverValidated
            ? 'Validated by the system · click to approve'
            : title
        }
        disabled={disabled || ids.length === 0 || saving}
        onClick={(e) => {
          e.stopPropagation();
          void validationStore.setStatus(ids, status);
        }}
        style={{ width: box, height: box }}
        className={[
          'tw-flex tw-items-center tw-justify-center tw-p-0 tw-m-0 tw-rounded tw-cursor-pointer tw-text-white disabled:tw-opacity-40 disabled:tw-cursor-not-allowed',
          current === status
            ? STATUS_META[status].bg
            : takesOverValidated
            ? STATUS_META.validated.bg
            : 'tw-bg-gray-500 hover:tw-bg-gray-400',
        ].join(' ')}
      >
        {savingStatus === status ? (
          <Loader2 size={px} className="tw-animate-spin" />
        ) : (
          statusIcon(status, px, font)
        )}
      </button>
    );
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className={[
        'tw-flex tw-items-center',
        size === 'lg' ? 'tw-gap-2' : 'tw-gap-1',
      ].join(' ')}
      onClick={(e) => e.stopPropagation()}
    >
      {btn('approved', 'Approve')}
      {btn('review', 'Mark for review')}
      {btn('rejected', 'Reject')}
    </div>
  );
};

/** Compact "N validated · N pending …" summary for the mode toolbar. */
export const ValidationCounts: React.FC<{
  counts: Record<ValidationStatus, number>;
  order: ValidationStatus[];
}> = ({ counts, order }) => (
  <span className="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-text-textDove">
    {order.map((status) => (
      <span key={status} className="tw-inline-flex tw-items-center tw-gap-1">
        <span
          className={[
            'tw-inline-block tw-w-2 tw-h-2 tw-rounded-full',
            STATUS_META[status].bg,
          ].join(' ')}
        />
        <span className="tw-text-white">{counts[status]}</span>
        <span>{STATUS_META[status].label.toLowerCase()}</span>
      </span>
    ))}
  </span>
);
